/**
 * Cognito InitiateAuth / RespondToAuthChallenge を直接呼び出す。
 * Hosted UI を使わず、自前ログインフォームで完結する。
 */
import { env } from '@/lib/env';

const COGNITO_ENDPOINT = `https://cognito-idp.${env.VITE_COGNITO_REGION}.amazonaws.com/`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function callCognito(action: string, payload: Record<string, unknown>): Promise<any> {
  const resp = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await resp.json();
  if (!resp.ok) {
    const errorType = (data.__type as string)?.split('#').pop() ?? 'UnknownError';
    const message = (data.message ?? data.Message ?? 'Unknown error') as string;
    throw new CognitoError(errorType, message);
  }
  return data;
}

export class CognitoError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'CognitoError';
  }
}

export interface AuthTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
}

export interface AuthResult {
  type: 'success';
  tokens: AuthTokens;
}

export interface NewPasswordRequired {
  type: 'new_password_required';
  session: string;
}

export type LoginResult = AuthResult | NewPasswordRequired;

/**
 * email + password でログイン。
 * 新規招待ユーザーの場合は NEW_PASSWORD_REQUIRED チャレンジが返る。
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const data = await callCognito('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: env.VITE_COGNITO_APP_CLIENT_ID,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  });

  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    return {
      type: 'new_password_required',
      session: data.Session as string,
    };
  }

  return {
    type: 'success',
    tokens: {
      access_token: data.AuthenticationResult.AccessToken as string,
      id_token: data.AuthenticationResult.IdToken as string,
      refresh_token: data.AuthenticationResult.RefreshToken as string,
    },
  };
}

/**
 * NEW_PASSWORD_REQUIRED チャレンジに応答して新パスワードを設定。
 */
export async function respondNewPassword(
  email: string,
  newPassword: string,
  session: string,
): Promise<AuthTokens> {
  const data = await callCognito('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: env.VITE_COGNITO_APP_CLIENT_ID,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
    },
    Session: session,
  });

  return {
    access_token: data.AuthenticationResult.AccessToken as string,
    id_token: data.AuthenticationResult.IdToken as string,
    refresh_token: data.AuthenticationResult.RefreshToken as string,
  };
}

/**
 * refresh token で新しい access/id token を取得。
 */
export async function refreshTokens(refreshToken: string): Promise<{ access_token: string; id_token: string }> {
  const data = await callCognito('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: env.VITE_COGNITO_APP_CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  });

  return {
    access_token: data.AuthenticationResult.AccessToken as string,
    id_token: data.AuthenticationResult.IdToken as string,
  };
}

/**
 * パスワードリセットのコード送信（メールに確認コードが届く）
 */
export async function forgotPassword(email: string): Promise<void> {
  await callCognito('ForgotPassword', {
    ClientId: env.VITE_COGNITO_APP_CLIENT_ID,
    Username: email,
  });
}

/**
 * 確認コード + 新パスワードでリセット完了
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await callCognito('ConfirmForgotPassword', {
    ClientId: env.VITE_COGNITO_APP_CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
}
